import { Injectable, resource, Signal } from '@angular/core';
import {
  AnalyticsInfo,
  AnalyticsRangeContext,
  DynamicQueryService,
} from '@services/analytics-services/core';
import { AggregatedStatDataService } from '@services/analytics-services/core/analytics-aggregated-stat.service';
import { ChartType, QueryType } from '@services/analytics-services/core/models/matrix-stats';
import { ProductAnalyticsService, ProductStats } from '@services/analytics-services/products';
import { DateRangePresetType } from '@services/core-services/models';
import { firstValueFrom } from 'rxjs';
import { FunnelChartData } from '@services/analytics-services/products/models/product-checkout-stats';
import { mapOverallCheckoutToFunnelData } from '@services/analytics-services/core/checkout-funnel.mapper';

@Injectable()
export class DashboardOverviewStore {
  constructor(
    private readonly productAnalyticsService: ProductAnalyticsService,
    private readonly aggregatedStatDataService: AggregatedStatDataService,
    private readonly dynamicQueryService: DynamicQueryService
  ) {}

  /**
   * Creates a resource for loading product analytics data
   */
  public createProductAnalyticsResource(context: Signal<AnalyticsRangeContext>) {
    return resource({
      params: () => context(),
      loader: ({ params }): Promise<AnalyticsInfo<ProductStats>> => {
        const { range, compare, interval, preset } = params;
        return firstValueFrom(
          this.productAnalyticsService.getAnalytics({
            preset: preset as DateRangePresetType,
            range,
            compare,
            interval,
          })
        );
      },
    });
  }

  /**
   * Creates a resource for loading checkout conversion stats
   */
  public createCheckoutConversionStatsResource(context: Signal<AnalyticsRangeContext>) {
    return resource({
      params: () => context(),
      loader: async ({ params }): Promise<FunnelChartData | undefined> => {
        const { range, interval, preset } = params;
        const stats = await firstValueFrom(
          this.aggregatedStatDataService.getAggregatedStatdata({
            preset: preset as DateRangePresetType,
            date_from: range.start,
            date_to: range.end,
            interval,
            query_type: QueryType.STATS,
            chart_type: ChartType.CHECKOUT_FUNNEL,
            timezone: this.dynamicQueryService.getUserTimezone(),
          })
        );

        if (!stats || stats?.length === 0) {
          console.warn('No checkout stats data available');
          return undefined;
        }

        const firstStat = stats[0];
        if (
          !firstStat ||
          typeof firstStat.total_carts_created !== 'number' ||
          typeof firstStat.total_orders_completed !== 'number'
        ) {
          throw new Error('Malformed checkout stats data');
        }

        const funnelData = mapOverallCheckoutToFunnelData(firstStat);
        console.log('Transformed funnel data:', funnelData); // Debug log
        return funnelData;
      },
    });
  }

  // Deprecated methods - keeping for backward compatibility during transition
  // TODO: Remove these once all components are migrated to resource pattern

  /**
   * @deprecated Use createProductAnalyticsResource instead
   */
  public loadProductsAnalytics(context: AnalyticsRangeContext) {
    return this.productAnalyticsService.getAnalytics({
      preset: context.preset as DateRangePresetType,
      range: context.range,
      compare: context.compare,
      interval: context.interval,
    });
  }

  /**
   * @deprecated Use createCheckoutConversionStatsResource instead
   */
  public loadCheckoutConversionStats(context: AnalyticsRangeContext) {
    return this.aggregatedStatDataService.getAggregatedStatdata({
      preset: context.preset as DateRangePresetType,
      date_from: context.range.start,
      date_to: context.range.end,
      interval: context.interval,
      query_type: QueryType.STATS,
      chart_type: ChartType.CHECKOUT_FUNNEL,
      timezone: this.dynamicQueryService.getUserTimezone(),
    });
  }
}
